'use client';

import { useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import {
  formatFindingStatusForDisplay,
  toasterApi,
  type InternalEnrichedFinding,
} from '@terra/ui-design-system';
import type { FindingStatusType } from '@terra/shared-schemas-orchestrator';
import { useMainContentTabs } from '../../../store/layoutStore';
import { ENTITY_TAB_TYPE } from '../../../store/layoutStoreTypes';
import { useUpdateFinding } from '../../../hooks/api/useFindings';
import { usePublishFinding } from '../../../hooks/api/usePublishFinding';
import { useFindingStatusReasonDialog } from '../../../hooks/useFindingStatusReasonDialog';
import {
  requiresUnpublishedFindingSignalConfirm,
  useUnpublishedFindingSignalConfirm,
} from '../../../hooks/useUnpublishedFindingSignalConfirm';

export function useFindingsRowStatusActions({
  onMutated,
}: {
  onMutated: () => Promise<void>;
}) {
  const tFindingsRow = useTranslations('findingsTab.row');
  const tCopilotTabTitles = useTranslations('chat.copilot.tabTitles');
  const { navigateInActiveTab } = useMainContentTabs();
  const { updateFinding, isUpdating } = useUpdateFinding();
  const { publishFinding } = usePublishFinding();
  const { confirmDisproveSignal, dialog: signalFalsePositiveDialog } =
    useUnpublishedFindingSignalConfirm();
  const pendingStatusFindingRef = useRef<InternalEnrichedFinding | null>(null);

  const editFinding = useCallback(
    (finding: InternalEnrichedFinding) =>
      navigateInActiveTab({
        id: `finding-edit-${finding.id}`,
        title: finding.title || tCopilotTabTitles('findingDetails'),
        type: ENTITY_TAB_TYPE.FINDING,
        data: { findingId: finding.id, mode: 'edit' },
      }),
    [navigateInActiveTab, tCopilotTabTitles],
  );
  const changeStatus = useCallback(
    async ({
      finding,
      status,
      note,
    }: {
      finding: InternalEnrichedFinding;
      status: FindingStatusType;
      note?: string;
    }): Promise<boolean> => {
      try {
        const needsSignalConfirm = requiresUnpublishedFindingSignalConfirm({
          isPublished: finding.isPublished,
          nextStatus: status,
          signalId: finding.signalId,
        });
        const disproveRelatedSignal = needsSignalConfirm
          ? await confirmDisproveSignal()
          : false;

        if (needsSignalConfirm && !disproveRelatedSignal) {
          return false;
        }

        const response = await updateFinding({
          findingId: finding.id,
          signalId: finding.signalId,
          patch: {
            status,
            ...(note ? { note } : {}),
            ...(disproveRelatedSignal ? { disproveRelatedSignal: true } : {}),
          },
        });

        if (response.status !== 200) {
          throw new Error(tFindingsRow('statusUpdateErrorDescription'));
        }

        toasterApi.success({
          title: tFindingsRow('statusUpdatedTitle'),
          description: tFindingsRow('statusUpdatedDescription', {
            status: formatFindingStatusForDisplay(status),
          }),
        });
        await onMutated();

        return true;
      } catch (error) {
        console.error('Failed to update finding status:', error);
        toasterApi.error({
          title: tFindingsRow('statusUpdateErrorTitle'),
          description:
            error instanceof Error
              ? error.message
              : tFindingsRow('statusUpdateErrorDescription'),
        });

        return false;
      }
    },
    [confirmDisproveSignal, onMutated, tFindingsRow, updateFinding],
  );
  const changeStatusRef = useRef(changeStatus);

  changeStatusRef.current = changeStatus;

  const { beginIfRequired, dialog: statusReasonDialog } =
    useFindingStatusReasonDialog({
      isSubmitting: isUpdating,
      onSubmit: async ({ status, note }) => {
        const finding = pendingStatusFindingRef.current;

        if (!finding) {
          return false;
        }

        const didUpdate = await changeStatusRef.current({
          finding,
          status,
          note,
        });

        if (didUpdate) {
          pendingStatusFindingRef.current = null;
        }

        return didUpdate;
      },
      onDismiss: () => {
        pendingStatusFindingRef.current = null;
      },
    });
  const requestStatusChange = useCallback(
    async (finding: InternalEnrichedFinding, status: FindingStatusType) => {
      if (
        beginIfRequired({ currentStatus: finding.status, nextStatus: status })
      ) {
        pendingStatusFindingRef.current = finding;

        return;
      }

      await changeStatus({ finding, status });
    },
    [beginIfRequired, changeStatus],
  );
  const togglePublish = useCallback(
    async (finding: InternalEnrichedFinding) => {
      const didUpdate = await publishFinding({
        findingId: finding.id,
        isPublished: !finding.isPublished,
      });

      if (didUpdate) {
        await onMutated();
      }
    },
    [onMutated, publishFinding],
  );

  return {
    editFinding,
    requestStatusChange,
    togglePublish,
    statusReasonDialog,
    signalFalsePositiveDialog,
  };
}
